import { useEffect, useMemo, useState } from "react";
import { Helmet } from "react-helmet";
import toast from "react-hot-toast";
import axiosInstance from "../../axiosInstance";
import PublicLayout from "../components/PublicLayout";

const fallbackContent = {
  title: "Contact Us",
  description: "<p>We're here to help! Reach out to our team and we'll get back to you as soon as possible.</p>",
};

const PublicContactPage = () => {
  const [page, setPage] = useState(null);
  const [homeData, setHomeData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [formData, setFormData] = useState({ name: "", email: "", subject: "", message: "" });

  useEffect(() => {
    const load = async () => {
      try {
        setLoading(true);
        const [pageResponse, homeResponse] = await Promise.all([
          axiosInstance.get("/pages/public/contact-us"),
          axiosInstance.get("/home-data"),
        ]);
        setPage(pageResponse.data?.page || null);
        setHomeData(homeResponse.data?.homeData || null);
      } catch (error) {
        setPage(null);
        setHomeData(null);
      } finally {
        setLoading(false);
      }
    };

    load();
  }, []);

  const content = useMemo(() => page || fallbackContent, [page]);

  const submitContact = async (event) => {
    event.preventDefault();
    try {
      setSubmitting(true);
      await axiosInstance.post("/contact-enquire/", formData);
      toast.success("Message sent successfully");
      setFormData({ name: "", email: "", subject: "", message: "" });
    } catch (error) {
      toast.error("Unable to send message right now");
    } finally {
      setSubmitting(false);
    }
  };

  const phone = homeData?.phone || "+91 00000 00000";
  const email = homeData?.email || "support@gojobify.com";
  const whatsapp = homeData?.whatsapp || phone.replace(/\D/g, "");

  return (
    <PublicLayout>
      <Helmet>
        <title>{content.metaTitle || `${content.title}  `}</title>
        <meta
          name="description"
          content={content.metaDescription || "Contact our support and hiring team."}
        />
      </Helmet>

      <style>{`
        .contact-redesign {
          background: linear-gradient(180deg, #f7f4ff 0%, #ffffff 48%, #f7f4ff 100%);
          padding: 44px 0 74px;
        }
        .contact-shell {
          max-width: 1160px;
          margin: 0 auto;
        }
        .contact-hero {
          position: relative;
          min-height: 390px;
          padding: 52px 18px;
          overflow: hidden;
        }
        .contact-hero::after {
          content: "";
          position: absolute;
          width: 230px;
          height: 230px;
          right: 16%;
          top: 110px;
          background: radial-gradient(circle, rgba(124, 58, 237, .16) 0 2px, transparent 3px);
          background-size: 18px 18px;
          opacity: .9;
        }
        .contact-plane {
          position: absolute;
          right: 44px;
          top: 190px;
          color: #8b5cf6;
          font-size: 64px;
          transform: rotate(-18deg);
          z-index: 1;
        }
        .contact-title {
          color: #080f28;
          font-size: clamp(2.35rem, 4vw, 4rem);
          font-weight: 900;
          line-height: 1.05;
          margin: 0 0 18px;
        }
        .contact-lead {
          max-width: 540px;
          color: #667085;
          font-size: 1.06rem;
          line-height: 1.75;
        }
        .contact-grid {
          display: grid;
          grid-template-columns: minmax(0, .95fr) minmax(0, 1.05fr);
          gap: 28px;
          align-items: start;
        }
        .contact-stack {
          display: grid;
          gap: 18px;
        }
        .contact-card {
          background: #fff;
          border: 1px solid #eee9ff;
          border-radius: 16px;
          box-shadow: 0 18px 42px rgba(50, 34, 120, .08);
          padding: 28px;
        }
        .contact-support-card {
          display: grid;
          grid-template-columns: 72px 1fr 24px;
          gap: 18px;
          align-items: center;
          min-height: 142px;
        }
        .contact-icon {
          width: 64px;
          height: 64px;
          border-radius: 50%;
          display: grid;
          place-items: center;
          color: #6d28d9;
          background: #f1edff;
          font-size: 30px;
          font-weight: 800;
        }
        .contact-icon.green { color: #12b76a; background: #ecfdf3; }
        .contact-icon.pink { color: #ec4899; background: #fdf2f8; }
        .contact-mini-label,
        .contact-detail-value {
          color: #6d28d9;
          font-weight: 800;
        }
        .contact-card h2,
        .contact-card h3 {
          color: #11182f;
          font-weight: 900;
        }
        .contact-muted {
          color: #667085;
          line-height: 1.65;
        }
        .contact-arrow {
          color: #7c3aed;
          font-size: 28px;
        }
        .contact-panel-title {
          position: relative;
          padding-bottom: 18px;
          margin-bottom: 26px;
        }
        .contact-panel-title::after {
          content: "";
          position: absolute;
          left: 0;
          bottom: 0;
          width: 78px;
          height: 3px;
          border-radius: 999px;
          background: linear-gradient(90deg, #6d28d9, #e9ddff);
        }
        .contact-detail-row {
          display: grid;
          grid-template-columns: 56px 1fr;
          gap: 16px;
          align-items: center;
          margin-bottom: 22px;
        }
        .contact-form label {
          color: #11182f;
          font-size: .92rem;
          font-weight: 800;
          margin-bottom: 8px;
        }
        .contact-input-wrap {
          position: relative;
        }
        .contact-input-wrap i {
          position: absolute;
          left: 18px;
          top: 50%;
          transform: translateY(-50%);
          color: #a4a7b5;
          font-size: 19px;
        }
        .contact-form .portal-input,
        .contact-form .portal-textarea,
        .contact-form select {
          width: 100%;
          border: 1px solid #e4e7ec;
          border-radius: 10px;
          min-height: 56px;
          padding: 0 16px 0 48px;
          color: #11182f;
          background: #fff;
          box-shadow: none;
        }
        .contact-form .portal-textarea {
          min-height: 150px;
          padding: 16px;
          resize: vertical;
        }
        .contact-submit {
          min-height: 58px;
          border-radius: 10px;
          background: linear-gradient(135deg, #6d28d9, #8b5cf6);
          box-shadow: 0 16px 28px rgba(109, 40, 217, .22);
        }
        .contact-whatsapp {
          background: linear-gradient(135deg, #7c3aed, #a855f7);
          color: #fff;
        }
        .contact-whatsapp .contact-muted,
        .contact-whatsapp h3 {
          color: #fff;
        }
        .contact-whatsapp-btn {
          display: inline-flex;
          align-items: center;
          gap: 8px;
          min-height: 48px;
          padding: 0 24px;
          border-radius: 8px;
          background: #fff;
          color: #11182f;
          text-decoration: none;
          font-weight: 800;
        }
        .contact-other-grid {
          display: grid;
          grid-template-columns: repeat(3, 1fr);
          gap: 14px;
          text-align: center;
        }
        @media (max-width: 991px) {
          .contact-grid { grid-template-columns: 1fr; }
          .contact-hero { min-height: auto; padding: 34px 0 24px; }
          .contact-plane { display: none; }
        }
        @media (max-width: 575px) {
          .contact-redesign { padding-top: 22px; }
          .contact-card { padding: 22px; border-radius: 14px; }
          .contact-support-card { grid-template-columns: 60px 1fr 18px; }
          .contact-icon { width: 54px; height: 54px; font-size: 25px; }
          .contact-other-grid { grid-template-columns: 1fr; }
        }
      `}</style>

      {loading ? (
        <section className="portal-loader-shell">
          <div className="portal-container">
            <div className="portal-loader-card">
              <div className="portal-round-loader" role="status" aria-label="Loading contact page"></div>
              <h2 className="h5 fw-bold mb-2">Loading contact page...</h2>
              <p className="text-muted mb-0">Preparing support and contact information.</p>
            </div>
          </div>
        </section>
      ) : (
        <section className="contact-redesign">
          <div className="portal-container">
            <div className="contact-shell">
              <div className="contact-hero">
                <h1 className="contact-title">{content.title}</h1>
                <div
                  className="contact-lead"
                  dangerouslySetInnerHTML={{ __html: content.description || fallbackContent.description }}
                />
                <div className="contact-plane"><i className="ri-send-plane-fill"></i></div>
              </div>

              <div className="contact-grid">
                <div className="contact-stack">
                  {[
                    { label: "24/7", title: "Fast Support", text: "Reach out for hiring help, recruiter onboarding, or candidate support.", icon: "ri-customer-service-2-line" },
                    { label: "HR", title: "Recruitment Guidance", text: "Our team can guide employers and job seekers through the platform.", icon: "ri-team-line" },
                    { label: "ETA", title: "Quick Response", text: "Most enquiries are reviewed promptly during working hours.", icon: "ri-flashlight-line" },
                  ].map((item) => (
                    <div className="contact-card contact-support-card" key={item.title}>
                      <div className="contact-icon"><i className={item.icon}></i></div>
                      <div>
                        <div className="contact-mini-label">{item.label}</div>
                        <h3 className="h5 mb-2">{item.title}</h3>
                        <p className="contact-muted mb-0">{item.text}</p>
                      </div>
                      <i className="ri-arrow-right-s-line contact-arrow"></i>
                    </div>
                  ))}

                  <div className="contact-card contact-whatsapp">
                    <div className="d-flex gap-3 align-items-center">
                      <div className="contact-icon"><i className="ri-chat-3-line"></i></div>
                      <div>
                        <h3 className="h5 mb-2">Need immediate help?</h3>
                        <p className="contact-muted mb-3">Chat with our support team on WhatsApp.</p>
                        <a
                          href={`https://wa.me/${whatsapp}`}
                          className="contact-whatsapp-btn"
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          <i className="ri-whatsapp-line text-success"></i> Chat on WhatsApp
                        </a>
                      </div>
                    </div>
                  </div>

                  <div className="contact-card">
                    <h2 className="h4 contact-panel-title">Contact Details</h2>
                    <div className="contact-detail-row">
                      <div className="contact-icon"><i className="ri-mail-fill"></i></div>
                      <div><strong>Email</strong><div className="contact-detail-value">{email}</div></div>
                    </div>
                    <div className="contact-detail-row">
                      <div className="contact-icon green"><i className="ri-phone-fill"></i></div>
                      <div><strong>Phone</strong><div className="contact-detail-value">{phone}</div></div>
                    </div>
                    <div className="contact-detail-row mb-0">
                      <div className="contact-icon pink"><i className="ri-map-pin-fill"></i></div>
                      <div><strong>Address</strong><div className="contact-muted">{homeData?.address || "India"}</div></div>
                    </div>
                  </div>
                </div>

                <aside className="contact-stack">
                  <div className="contact-card">
                    <h2 className="h4 contact-panel-title">Send a Message</h2>
                    <form onSubmit={submitContact} className="contact-form">
                      <div className="mb-3">
                        <label className="d-block">Full Name</label>
                        <div className="contact-input-wrap">
                          <i className="ri-user-line"></i>
                          <input
                            className="portal-input"
                            value={formData.name}
                            placeholder="Enter your full name"
                            onChange={(event) =>
                              setFormData((prev) => ({ ...prev, name: event.target.value }))
                            }
                            required
                          />
                        </div>
                      </div>
                      <div className="mb-3">
                        <label className="d-block">Email Address</label>
                        <div className="contact-input-wrap">
                          <i className="ri-mail-line"></i>
                          <input
                            type="email"
                            className="portal-input"
                            value={formData.email}
                            placeholder="Enter your email address"
                            onChange={(event) =>
                              setFormData((prev) => ({ ...prev, email: event.target.value }))
                            }
                            required
                          />
                        </div>
                      </div>
                      <div className="mb-3">
                        <label className="d-block">Subject</label>
                        <select
                          aria-label="Select a subject"
                          value={formData.subject}
                          onChange={(event) =>
                            setFormData((prev) => ({ ...prev, subject: event.target.value }))
                          }
                          required
                        >
                          <option value="" disabled>Select a subject</option>
                          <option>Hiring Support</option>
                          <option>Candidate Support</option>
                          <option>Partnership</option>
                        </select>
                      </div>
                      <div className="mb-3">
                        <label className="d-block">How can we help?</label>
                        <textarea
                          className="portal-textarea"
                          rows="5"
                          placeholder="Type your message here..."
                          value={formData.message}
                          onChange={(event) =>
                            setFormData((prev) => ({ ...prev, message: event.target.value }))
                          }
                          required
                        />
                      </div>
                      <p className="contact-muted small d-flex align-items-start gap-2 mb-4">
                        <i className="ri-shield-check-line text-primary fs-5"></i>
                        We respect your privacy. Your information is safe with us.
                      </p>
                      <button className="portal-btn-primary contact-submit w-100" disabled={submitting}>
                        {submitting ? "Sending..." : "Submit Inquiry"} <i className="ri-send-plane-fill"></i>
                      </button>
                    </form>
                  </div>

                  <div className="contact-card">
                    <h2 className="h5 fw-bold mb-4">Other Ways to Reach Us</h2>
                    <div className="contact-other-grid">
                      <a className="text-decoration-none" href={`https://wa.me/${whatsapp}`} target="_blank" rel="noopener noreferrer">
                        <div className="contact-icon green mx-auto mb-2"><i className="ri-whatsapp-line"></i></div>
                        <strong className="text-dark d-block">WhatsApp</strong>
                        <span className="text-primary small fw-bold">Chat Now</span>
                      </a>
                      <a className="text-decoration-none" href={`mailto:${email}`}>
                        <div className="contact-icon mx-auto mb-2"><i className="ri-mail-fill"></i></div>
                        <strong className="text-dark d-block">Email</strong>
                        <span className="text-primary small fw-bold">Send Mail</span>
                      </a>
                      <a className="text-decoration-none" href={`tel:${phone}`}>
                        <div className="contact-icon mx-auto mb-2"><i className="ri-phone-fill"></i></div>
                        <strong className="text-dark d-block">Call Us</strong>
                        <span className="contact-muted small">Mon - Sat</span>
                      </a>
                    </div>
                  </div>
                </aside>
              </div>
            </div>
          </div>
        </section>
      )}
    </PublicLayout>
  );
};

export default PublicContactPage;
